import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { marketingCheckStatusTone } from '@/temu-platform/utils/temuMarketingDisplay';
import MarketingSitesCell from '@/temu-platform/modules/ops/components/MarketingSitesCell';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-drawer.css';

function RequirementList({ title, items }) {
  const { t } = useTranslation('workbench');
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <section className="tpo-marketing-drawer__section">
        <h3>{title}</h3>
        <p className="tpo-marketing-drawer__muted">{fixedUi("暂无数据")}</p>
      </section>
    );
  }
  return (
    <section className="tpo-marketing-drawer__section">
      <h3>{title}</h3>
      <ul className="tpo-marketing-drawer__req-list">
        {items.map((item, idx) => {
          const statusText = item?.checkStatusDesc
            || (item?.checkStatus == null ? '' : `状态 ${item.checkStatus}`);
          const tone = marketingCheckStatusTone(item?.checkStatus, item?.checkStatusDesc);
          return (
            <li key={item?.requirementCode ?? idx}>
              <strong>{item?.requirementDesc || item?.requirementType || t('marketing.enReqN', { n: idx + 1 })}</strong>
              {statusText ? (
                <span className={`tpo-marketing-drawer__req-status tpo-marketing-drawer__req-status--${tone}`}>
                  {statusText}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 活动详情抽屉（只读）：详情 / 可报商品 / 场次
 * 报名请到独立页 TemuMarketingEnrollPage
 */
export default function TemuMarketingActivityDrawer({
  open,
  shopId,
  activity,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [subTab, setSubTab] = useState('detail');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detail, setDetail] = useState(null);

  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [products, setProducts] = useState([]);
  const [scrollContext, setScrollContext] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [sessions, setSessions] = useState([]);

  const thematicId = activity?.activityThematicId || activity?.activityId;
  const activityType = Number(activity?.activityType);

  const loadDetail = useCallback(async () => {
    if (!shopId || !thematicId || !Number.isFinite(activityType)) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await temuPlatformAPI.getMarketingActivityDetail(shopId, thematicId, { activityType });
      setDetail(res?.result || null);
      if (res?.success === false && res?.errorMsg) {
        setDetailError(res.errorMsg);
      }
    } catch (e) {
      setDetail(null);
      setDetailError(e?.message || '加载活动详情失败');
    } finally {
      setDetailLoading(false);
    }
  }, [shopId, thematicId, activityType]);

  const loadProducts = useCallback(async ({ reset = false, context } = {}) => {
    if (!shopId || !thematicId || !Number.isFinite(activityType)) return;
    setProductsLoading(true);
    setProductsError('');
    try {
      const nextContext = reset ? undefined : (context !== undefined ? context : (scrollContext || undefined));
      const res = await temuPlatformAPI.getMarketingActivityProducts(shopId, thematicId, {
        activityType,
        rowCount: 20,
        searchScrollContext: nextContext,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setScrollContext(res?.searchScrollContext || null);
      setHasMore(Boolean(res?.hasMore));
      if (res?.success === false && res?.errorMsg) {
        setProductsError(res.errorMsg);
      }
    } catch (e) {
      if (reset) setProducts([]);
      setProductsError(e?.message || '加载活动商品失败');
    } finally {
      setProductsLoading(false);
    }
  }, [shopId, thematicId, activityType, scrollContext]);

  const loadSessions = useCallback(async (productId) => {
    if (!shopId || !thematicId || !Number.isFinite(activityType) || !productId) {
      setSessions([]);
      return;
    }
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const res = await temuPlatformAPI.getMarketingActivitySessions(shopId, thematicId, {
        activityType,
        productIds: [productId],
      });
      setSessions(Array.isArray(res?.items) ? res.items : []);
      if (res?.success === false && res?.errorMsg) {
        setSessionsError(res.errorMsg);
      }
    } catch (e) {
      setSessions([]);
      setSessionsError(e?.message || '加载场次失败');
    } finally {
      setSessionsLoading(false);
    }
  }, [shopId, thematicId, activityType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !activity) return;
    setSubTab('detail');
    setDetail(null);
    setProducts([]);
    setScrollContext(null);
    setHasMore(false);
    setSelectedProductId(null);
    setSessions([]);
    setDetailError('');
    setProductsError('');
    setSessionsError('');
    loadDetail();
  }, [open, activity, loadDetail]);

  useEffect(() => {
    if (!open || subTab !== 'products') return;
    setProducts([]);
    setScrollContext(null);
    setHasMore(false);
    loadProducts({ reset: true, context: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subTab, thematicId, activityType]);

  if (!open) return null;

  const thematicInfo = detail?.thematicInfo || detail?.activityInfo || {};
  const canEnroll = detail?.activityInfo?.canEnroll ?? detail?.canEnroll;

  return (
    <div className="temu-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="temu-drawer temu-drawer--wide"
        role="dialog"
        aria-labelledby="tpo-marketing-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="temu-drawer__header">
          <div>
            <h2 id="tpo-marketing-drawer-title">{activity?.name || activity?.activityName || t('marketing.enDetail')}</h2>
            <p className="temu-drawer__subtitle">
              {activity?.activityTypeLabel || activity?.type || '—'}
              {thematicId ? ` · 专题 ${thematicId}` : ''}
            </p>
          </div>
          <button type="button" className="temu-drawer__close" onClick={onClose} aria-label={t('moduleShell.close')}>
            ×
          </button>
        </header>

        <div className="tpo-marketing-drawer__tabs" role="tablist">
          {[
            { id: 'detail', label: '详情' },
            { id: 'products', label: '可报商品' },
            { id: 'sessions', label: '场次' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={`tpo-status-tab${subTab === tab.id ? ' is-active' : ''}`}
              aria-selected={subTab === tab.id}
              onClick={() => setSubTab(tab.id)}
            >
              {fixedUi(tab.label)}
            </button>
          ))}
        </div>

        <div className="temu-drawer__body">
          {subTab === 'detail' ? (
            <>
              {detailLoading ? <p className="temu-drawer__hint">{fixedUi("加载详情中…")}</p> : null}
              {!detailLoading && detailError ? <p className="temu-drawer__error">{detailError}</p> : null}
              {!detailLoading && !detailError && detail ? (
                <div className="tpo-marketing-drawer__detail">
                  <div className="temu-detail-grid">
                    <div className="temu-detail-row">
                      <span className="temu-detail-row__label">{fixedUi("是否可报名")}</span>
                      <span className="temu-detail-row__value">
                        {canEnroll == null ? '—' : (canEnroll ? fixedUi("是") : fixedUi("否"))}
                      </span>
                    </div>
                    <div className="temu-detail-row">
                      <span className="temu-detail-row__label">{fixedUi("活动名称")}</span>
                      <span className="temu-detail-row__value">
                        {thematicInfo.activityThematicName
                          || detail?.activityInfo?.activityName
                          || activity?.name
                          || '—'}
                      </span>
                    </div>
                    <div className="temu-detail-row">
                      <span className="temu-detail-row__label">{fixedUi("报名方式")}</span>
                      <span className="temu-detail-row__value">
                        {thematicInfo.enrollSource === 1
                          ? fixedUi("自主报名")
                          : (thematicInfo.enrollSource === 0
                            ? '邀约报名'
                            : (activity?.enrollSourceLabel
                              || (Number(activity?.enrollSource) === 1
                                ? fixedUi("自主报名")
                                : (Number(activity?.enrollSource) === 0 ? '邀约报名' : '—'))))}
                      </span>
                    </div>
                  </div>
                  <RequirementList title={fixedUi("活动要求")} items={detail?.requirements} />
                  <RequirementList title={fixedUi("店铺资质")} items={detail?.mallAptitude} />
                </div>
              ) : null}
              {!detailLoading && !detailError && !detail ? (
                <p className="temu-drawer__hint">{fixedUi("暂无详情")}</p>
              ) : null}
            </>
          ) : null}

          {subTab === 'products' ? (
            <>
              {productsError ? <p className="temu-drawer__error">{productsError}</p> : null}
              <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
                <table className="tpo-publish__table tpo-drafts__table tpo-ops-table">
                  <thead>
                    <tr>
                      <th scope="col">{fixedUi("货品 ID")}</th>
                      <th scope="col">{fixedUi("名称")}</th>
                      <th scope="col">{fixedUi("建议库存")}</th>
                      <th scope="col">{fixedUi("站点")}</th>
                      <th scope="col">{fixedUi("操作")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 && !productsLoading ? (
                      <tr>
                        <td colSpan={5} className="tpo-certs-empty-cell">{fixedUi("暂无可报商品")}</td>
                      </tr>
                    ) : (
                      products.map((row) => (
                        <tr
                          key={String(row.productId)}
                          className={String(selectedProductId) === String(row.productId) ? 'is-selected' : ''}
                        >
                          <td>{row.productId ?? '—'}</td>
                          <td>{row.productName || '—'}</td>
                          <td>{row.suggestActivityStock ?? row.targetActivityStock ?? '—'}</td>
                          <td><MarketingSitesCell row={row} /></td>
                          <td>
                            <button
                              type="button"
                              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                              onClick={() => {
                                setSelectedProductId(row.productId);
                                setSubTab('sessions');
                                loadSessions(row.productId);
                              }}
                            >
                              {t('marketing.enViewSessions')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="tpo-marketing-drawer__foot">
                {productsLoading ? <span className="tpo-marketing-drawer__muted">{fixedUi("加载中…")}</span> : null}
                {hasMore && !productsLoading ? (
                  <button
                    type="button"
                    className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                    onClick={() => loadProducts({ reset: false })}
                  >
                    {t('marketing.enLoadMore')}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}

          {subTab === 'sessions' ? (
            <>
              <p className="tpo-marketing-drawer__muted">
                {t('marketing.enNeedProduct')}
              </p>
              <div className="tpo-marketing-drawer__session-pick">
                <input
                  className="tpo-input"
                  type="text"
                  placeholder={fixedUi("货品 productId")}
                  value={selectedProductId == null ? '' : String(selectedProductId)}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    setSelectedProductId(v ? Number(v) : null);
                  }}
                />
                <button
                  type="button"
                  className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                  disabled={!selectedProductId || sessionsLoading}
                  onClick={() => {
                    if (!selectedProductId) {
                      showToast(fixedUi("请先填写货品 ID"), 'warning');
                      return;
                    }
                    loadSessions(selectedProductId);
                  }}
                >
                  {t('marketing.enQuerySessions')}
                </button>
              </div>
              {sessionsError ? <p className="temu-drawer__error">{sessionsError}</p> : null}
              {sessionsLoading ? <p className="temu-drawer__hint">{fixedUi("加载场次中…")}</p> : null}
              <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
                <table className="tpo-publish__table tpo-drafts__table tpo-ops-table">
                  <thead>
                    <tr>
                      <th scope="col">{fixedUi("场次")}</th>
                      <th scope="col">{fixedUi("状态")}</th>
                      <th scope="col">{fixedUi("站点")}</th>
                      <th scope="col">{fixedUi("开始")}</th>
                      <th scope="col">{fixedUi("结束")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!sessionsLoading && sessions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="tpo-certs-empty-cell">{fixedUi("暂无场次")}</td>
                      </tr>
                    ) : (
                      sessions.map((row) => (
                        <tr key={String(row.sessionId || `${row.sessionName}-${row.startTime}`)}>
                          <td>{row.sessionName || row.sessionId || '—'}</td>
                          <td>{row.sessionStatusLabel || '—'}</td>
                          <td>{row.siteName || row.siteId || '—'}</td>
                          <td>{row.startTime || row.startDateStr || '—'}</td>
                          <td>{row.endTime || row.endDateStr || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
